import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaTrash, FaEdit, FaUsers, FaEnvelope, FaPaperPlane } from "react-icons/fa";
import api from "../../api";
import UserEditModal from "./UserEditModal";
import { isOrgAdmin } from "../../utils/roles";

const PERM_KEYS = [
  { key: "dashboard",    navKey: "navbar.dashboard" },
  { key: "quote",        navKey: "navbar.quotes" },
  { key: "missions",     navKey: "navbar.missions" },
  { key: "invoices",     navKey: "navbar.invoices" },
  { key: "salaries",     navKey: "navbar.salaries" },
  { key: "investors",    navKey: "navbar.investors" },
  { key: "appointments", navKey: "navbar.calendar" },
  { key: "subcontractors", navKey: "navbar.subcontractors" },
];

const UserPage = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", email: "", role: "user", permissions: [] });
  const [editingId, setEditingId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (err) {
      toast.error(t("user.fetchError"));
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/users/${editingId}`, form);
        toast.success(t("user.updated"));
      } else {
        await api.post("/users", { ...form, permissions: form.permissions || [] });
        toast.success(t("user.invitedSuccess", "Utilisateur créé — invitation envoyée par email."));
      }
      setForm({ username: "", email: "", role: "user", permissions: [] });
      setEditingId(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || t("user.saveError"));
    }
  };

  const handleResendInvite = async (id) => {
    try {
      await api.post(`/users/${id}/resend-invite`);
      toast.success(t("user.inviteResent", "Invitation renvoyée."));
    } catch (err) {
      toast.error(err?.response?.data?.message || t("user.saveError"));
    }
  };

  const handleDelete = (id) => setConfirmDeleteId(id);

  const confirmDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await api.delete(`/users/${id}`);
      toast.success(t("user.deleted"));
      fetchUsers();
    } catch (err) {
      toast.error(t("user.deleteError"));
    }
  };

  const handleEdit = (user) => {
    setShowEditModal(user);
  };

  const handlePermissionToggle = async (user, permKey) => {
    if (isOrgAdmin(user)) {
      toast.info(t("user.adminPermsInfo"));
      return;
    }
    const permissions = Array.isArray(user.permissions) ? [...user.permissions] : [];
    const hasPerm = permissions.includes(permKey);
    const newPerms = hasPerm
      ? permissions.filter((p) => p !== permKey)
      : [...permissions, permKey];
    try {
      await api.put(`/users/${user.id}`, { permissions: newPerms });
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, permissions: newPerms } : u))
      );
      toast.success(t("user.permUpdated"));
    } catch (err) {
      toast.error(t("user.permUpdateError"));
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <FaUsers className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-base-content">{t("user.title")}</h1>
          <p className="text-sm text-base-content/40 mt-0.5">{t("user.subtitle")}</p>
        </div>
      </div>

      {/* Create / Edit Form */}
      <div className="bg-base-100 border border-base-200 rounded-xl shadow-sm p-6 mb-8">
        <h2 className="text-base font-semibold text-base-content mb-4">
          {editingId ? t("user.editUser") : t("user.createNew")}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-base-content/70 mb-1">{t("user.username")}</label>
              <input
                type="text"
                required
                className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-base-content/70 mb-1">{t("user.email", "Email")}</label>
              <input
                type="email"
                required
                placeholder={t("user.emailPlaceholder", "email@exemple.com")}
                className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <p className="text-xs text-base-content/40 mt-1">
                {t("user.inviteHint", "Un lien d'activation sera envoyé à cette adresse.")}
              </p>
            </div>
          </div>

          <div className="w-full sm:w-48">
            <label className="block text-sm font-medium text-base-content/70 mb-1">{t("user.role")}</label>
            <select
              className="select select-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              {/* Un org_admin ne crée que des membres. Les rôles privilégiés sont
                  attribués par le superadmin via la console /admin. */}
              <option value="user">user</option>
            </select>
          </div>

          {form.role === "user" && (
            <div>
              <label className="block text-sm font-medium text-base-content/70 mb-2">{t("user.permissions")}</label>
              <div className="flex flex-wrap gap-3">
                {PERM_KEYS.map((perm) => (
                  <label
                    key={perm.key}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-base-200 bg-base-200 cursor-pointer hover:border-primary/40 transition-colors text-sm text-base-content/70"
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-primary checkbox-xs"
                      checked={form.permissions.includes(perm.key)}
                      onChange={() => {
                        const checked = form.permissions.includes(perm.key);
                        setForm((prev) => ({
                          ...prev,
                          permissions: checked
                            ? prev.permissions.filter((p) => p !== perm.key)
                            : [...prev.permissions, perm.key],
                        }));
                      }}
                    />
                    {t(perm.navKey)}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            {editingId && (
              <button
                type="button"
                onClick={() => { setForm({ username: "", email: "", role: "user", permissions: [] }); setEditingId(null); }}
                className="btn btn-ghost mr-2"
              >
                {t("buttons.cancel")}
              </button>
            )}
            <button type="submit" className="btn btn-primary">
              {editingId ? t("user.update") : t("user.create")}
            </button>
          </div>
        </form>
      </div>

      {/* Users Table */}
      <h3 className="text-base font-semibold text-base-content mb-3">{t("user.existingUsers")}</h3>
      <div className="overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm">
        <table className="table table-sm">
          <thead className="bg-base-200 border-b border-base-200">
            <tr>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3">{t("user.username")}</th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3">{t("user.role")}</th>
              {PERM_KEYS.map((perm) => (
                <th key={perm.key} className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3 text-center">
                  {t(perm.navKey)}
                </th>
              ))}
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3">{t("mission.list.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-200">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-base-200/50 transition-colors group">
                <td className="py-3 font-medium text-base-content text-sm">
                  <div className="flex items-center gap-2">
                    <span>{u.username}</span>
                    {u.invitePending && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/20" title={u.email}>
                        <FaEnvelope size={9} /> {t("user.pending", "Invitation en attente")}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${
                    isOrgAdmin(u)
                      ? "bg-primary/10 text-primary border-primary/20"
                      : "bg-base-200 text-base-content/60 border-base-300"
                  }`}>
                    {u.role}
                  </span>
                </td>
                {PERM_KEYS.map((perm) => (
                  <td key={perm.key} className="py-3 text-center">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-primary checkbox-xs"
                      checked={isOrgAdmin(u) ? true : (u.permissions || []).includes(perm.key)}
                      disabled={isOrgAdmin(u)}
                      onChange={() => handlePermissionToggle(u, perm.key)}
                    />
                  </td>
                ))}
                <td className="py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {u.email && (
                      <button
                        onClick={() => handleResendInvite(u.id)}
                        className="w-7 h-7 rounded-lg text-base-content/40 hover:text-primary hover:bg-primary/10 flex items-center justify-center transition-colors"
                        title={t("user.resendInvite", "Renvoyer l'invitation")}
                      >
                        <FaPaperPlane className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(u)}
                      className="w-7 h-7 rounded-lg text-base-content/40 hover:text-base-content hover:bg-base-300 flex items-center justify-center transition-colors"
                      title={t("buttons.edit")}
                    >
                      <FaEdit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(u.id)}
                      className="w-7 h-7 rounded-lg text-base-content/40 hover:text-error hover:bg-error/10 flex items-center justify-center transition-colors"
                      title={t("buttons.delete")}
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={2 + PERM_KEYS.length + 1} className="py-16 text-center text-base-content/40 text-sm">
                  {t("salary.noResults")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Confirm Delete Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-xl border border-base-200 shadow-2xl p-6 max-w-sm w-full animate-fade-in">
            <h3 className="font-bold text-base-content text-base mb-1">{t("user.confirmDelete")}</h3>
            <p className="text-sm text-base-content/50 mb-5">{t("user.deleteIrreversible", "Cette action est irréversible.")}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDeleteId(null)} className="btn btn-ghost btn-sm">{t("buttons.cancel")}</button>
              <button onClick={confirmDelete} className="btn btn-error btn-sm">{t("buttons.delete")}</button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <UserEditModal
          user={showEditModal}
          onClose={() => setShowEditModal(null)}
          onSuccess={fetchUsers}
        />
      )}
    </div>
  );
};

export default UserPage;
